import React, { forwardRef } from "react";
import { cn } from "../lib/utils.js";
import { DisclosureCaret } from "./DisclosureCaret.js";
import { useDisclosureContext } from "./disclosure-context.js";

/* DisclosureTrigger — the button that opens and closes its DisclosureRoot's
   panel. It owns the aria-expanded / aria-controls wiring, so the pair holds
   wherever the panel is rendered. Keep other controls OUT of it: a checkbox or
   a menu inside a button toggles the panel and joins its accessible name. */

export interface DisclosureTriggerProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "type" | "aria-expanded" | "aria-controls"
> {
  /** Rendered before the children in a DisclosureCaret, which turns it. */
  caret?: React.ReactNode;
}

export const DisclosureTrigger = forwardRef<
  HTMLButtonElement,
  DisclosureTriggerProps
>(function DisclosureTrigger(
  { caret, className, onClick, children, ...rest },
  ref,
) {
  const { open, panelId, toggle } = useDisclosureContext("DisclosureTrigger");

  return (
    <button
      ref={ref}
      {...rest}
      type="button"
      aria-expanded={open}
      aria-controls={panelId}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) toggle();
      }}
      className={cn(
        "flex w-full items-center gap-2 text-start hover-quiet press focus-ring",
        className,
      )}
    >
      {caret ? <DisclosureCaret>{caret}</DisclosureCaret> : null}
      {children}
    </button>
  );
});
